import { Dictionary } from '@ngrx/entity';

/**
 * Checks if a node is a child (descendant) of a parent node in a nested set model tree.
 *
 * @param parent - The potential parent node
 * @param child - The potential child node
 * @returns True if child is a descendant of parent, false otherwise
 */
export function isChild<T extends TreeBranchInterface>(parent: T, child: T): boolean {
  if (!parent) return false;
  return parent.treeId === child.treeId && parent.lft < child.lft && child.rgt < parent.rgt;
}

export interface TreeBranchInterface {
  id?: number;
  treeId: number;
  rgt: number;
  lft: number;
  depth: number;
  children?: TreeBranchInterface[];
  parent?: TreeBranchInterface;
}

export function makeTree<T extends TreeBranchInterface>(data: T[]) {
  const clone = JSON.parse(JSON.stringify(data));
  const branch: T[] = [];

  // check if there are leaves (children) in the tree we passed
  while (clone.length) {
    // get the first element to process and remove it from the tree
    const currentBranch: T = { ...clone.shift() };

    // find the number of leaves in our branch (including sub-branches)
    // when there are no more sub-branches this will be 0
    let leaves: number = (currentBranch.rgt - currentBranch.lft - 1) / 2;
    // since we ordered by 'lft', the next x (= leaves) rows contain all child nodes
    // we get those nodes and remove them from the tree
    for (let i = leaves; i >= 0; i--) {
      // if the tree is incomplete, there might be less leaves
      // we search for the last leaf that's still a child of the current branch
      leaves = i;
      if (i === 0) break; // there are no children for this branch

      const leaf = clone[i - 1];
      if (leaf && leaf.lft < currentBranch.rgt) break;
    }
    const children: T[] = clone.splice(0, leaves).map((leaf) => ({ ...leaf, parent: currentBranch }));

    // build the subtree with our child rows and append it to the current element
    currentBranch.children = makeTree(children);

    // append the current element (with sub-branches) to the current branch
    branch.push(currentBranch);
  }

  // return the (sub)tree
  return branch;
}

export function findParent<T extends TreeBranchInterface>(currentBranch: T, branchesToSearch: T[]): T {
  return branchesToSearch.find(
    (parentToc: T) =>
      parentToc.depth === currentBranch.depth - 1 &&
      currentBranch.lft > parentToc.lft &&
      currentBranch.rgt < parentToc.rgt
  );
}

export function findTopMostParentInTreeById<T extends TreeBranchInterface>(branchesToSearch: T[], id: number) {
  let found: T;
  branchesToSearch.some((node) => {
    if (node.id && node.id === id) {
      return (found = node);
    }
    if (node.children) {
      const foundChild = findTopMostParentInTreeById(node.children, id);
      if (foundChild) {
        return (found = node);
      }
    }
    return false;
  });
  return found;
}

export function findToc<T extends TreeBranchInterface>(branchesToSearch: T[], id: number): T {
  let found: T;

  branchesToSearch.some((node) => {
    if (node.id && node.id === id) {
      return (found = node);
    }
    if (node.children) {
      const foundChild = findToc(node.children, id);
      if (foundChild) {
        return (found = foundChild as T);
      }
    }
    return false;
  });
  return found;
}

export function makeTreeFromIncomplete<T extends TreeBranchInterface>(data: T[]) {
  const tree: T[] = [];

  const branchDict: Dictionary<T> = {}; // hold references to branches in tree
  const rowDict: Dictionary<T> = {}; // hold references to original data (used for parent prop, to prevent circular data structures)

  const isDirectChild = (parent: T, child: T) =>
    parent.treeId === child.treeId &&
    parent.depth === child.depth - 1 &&
    parent.lft < child.lft &&
    child.rgt < parent.rgt;

  // because tocs are sorted by lft, a toc is either:
  // - a root element (depth === 0)
  // - a child of the previous toc (=> data[i-1])
  // - a child of one of the parents of the previous toc (=> check parents recursively)
  const addBranchToTree = (branch: T, parentBranch: T) => {
    if (branch.depth === 0) {
      tree.push(branch);
    } else if (!parentBranch) {
      console.warn(`no parent found in provided data for id ${branch.id}`);
    } else if (isDirectChild(parentBranch, branch)) {
      branch.parent = rowDict[parentBranch.id];
      parentBranch.children.push(branch);
    } else {
      addBranchToTree(branch, branchDict[parentBranch.parent?.id]);
    }
  };

  data.forEach((row, i) => {
    const branch = { ...row, children: [] };
    branchDict[branch.id] = branch;
    rowDict[row.id] = row;

    const previousBranch = branchDict[data[i - 1]?.id];
    addBranchToTree(branch, previousBranch);
  });

  return tree;
}

export function flatten<T extends TreeBranchInterface>(tree: T[]): T[] {
  const arr = [];

  const concatBranches = ({ children = [], parent, ...toc }: T) => {
    arr.push(toc as T);
    children.forEach(concatBranches);
  };

  tree.forEach(concatBranches);

  return arr;
}

/**
 * Gets the direct children of a parent node in a nested set model tree.
 *
 * In the nested set model, direct children are nodes that:
 * - Share the same treeId as the parent
 * - Have lft values greater than parent's lft
 * - Have rgt values less than parent's rgt
 * - Have depth exactly one level deeper than parent (depth = parent.depth + 1)
 *
 * @template T - Type extending TreeBranchInterface
 * @param parent - The parent node to find children for
 * @param allNodes - Array of all nodes to search through
 * @returns Array of direct child nodes
 *
 * @example
 * ```typescript
 * const chapter = { id: 1, treeId: 100, lft: 2, rgt: 7, depth: 0 };
 * const allTocs = [
 *   { id: 2, treeId: 100, lft: 3, rgt: 4, depth: 1 }, // lesson 1
 *   { id: 3, treeId: 100, lft: 5, rgt: 6, depth: 1 }, // lesson 2
 * ];
 * const lessons = getDirectChildren(chapter, allTocs);
 * ```
 */
export function getDirectChildren<T extends TreeBranchInterface>(parent: T, allNodes: T[]): T[] {
  if (!parent || !allNodes?.length) {
    return [];
  }

  return allNodes.filter(
    (node) =>
      node.treeId === parent.treeId && node.lft > parent.lft && node.rgt < parent.rgt && node.depth === parent.depth + 1
  );
}

export function getSiblings<T extends TreeBranchInterface>(
  node: T,
  allNodes: T[],
  options: { includeSelf: boolean } = { includeSelf: true } // include node itself by default
): T[] {
  if (!node || !allNodes?.length) {
    return [];
  }

  const filterSelf = options.includeSelf ? (_sibling: T) => true : (sibling: T) => sibling.id !== node.id;

  const parent = findParent(node, allNodes);
  if (!parent) {
    // return top level nodes
    return allNodes.filter(
      (sibling) => filterSelf(sibling) && sibling.treeId === node.treeId && sibling.depth === node.depth
    );
  }
  const siblings = allNodes.filter(
    (sibling) =>
      filterSelf(sibling) &&
      sibling.treeId === node.treeId &&
      sibling.depth === node.depth &&
      parent.lft < sibling.lft &&
      parent.rgt > sibling.rgt
  );

  return siblings;
}

export interface TreeBranchMinimalInterface {
  children: TreeBranchInterface[]; // force children to be present
  parent?: TreeBranchInterface;
}

export interface FilterTreeOptions {
  keepMatchingLeafs?: boolean;
}

/**
 * Recursively filters a tree based on a predicate function.
 *
 * Nodes are kept if:
 * 1. They have at least one descendant that is kept (implicit keep)
 * 2. OR they match the predicate AND are allowed to be kept as leafs (options.keepMatchingLeafs)
 *
 * @template T - The type of tree nodes, must extend TreeBranchMinimalInterface
 * @param tree - The input tree array (array of root nodes)
 * @param predicate - Function that returns true if a node should be kept
 * @param options - Configuration options for filtering
 * @param options.keepMatchingLeafs - If true (default), nodes matching the predicate are kept even if they have no kept children.
 *                                    If false, nodes are only kept if they have kept children (meaning they are effectively removed if they become leafs).
 * @returns A new array containing the filtered tree structure
 */
export function filter<T extends TreeBranchMinimalInterface>(
  tree: T[],
  predicate: (node: T) => boolean,
  options: FilterTreeOptions = { keepMatchingLeafs: true }
): T[] {
  const { keepMatchingLeafs = true } = options;
  const filterNode = (node: T): T | null => {
    const children = ((node.children as unknown as T[]) ?? [])
      .map(filterNode)
      .filter((child): child is T => child !== null);

    const newNode = { ...node, children };

    // nodes with matching children are always kept
    if (children.length > 0) {
      return newNode;
    }

    // nodes without children are only kept if they match the predicate and options allow it
    if (predicate(newNode) && keepMatchingLeafs) {
      return newNode;
    }

    return null;
  };

  return (tree || []).map(filterNode).filter((node): node is T => node !== null);
}
